/**
 * Inline EHR picker for the Projects agent.
 *
 * Used when no EHR is connected and the agent must ask which one the
 * clinician uses: it calls ``ask_user`` with ``kind: 'pick_ehr'`` and
 * this list renders the supported EHR providers (self-fetched from the
 * canonical ``EHRProvider`` enum — the agent never enumerates them, so
 * the option set is code-owned, not model-authored). The selected
 * ``{ id, label }`` is handed back via ``onSelect`` and the
 * clarification resolves with the chosen provider id.
 *
 * Standalone + presentational: it owns the search + selection highlight,
 * but not the submit — the parent decides what "picking" means (here:
 * arm the clarification's Continue button). The provider display names
 * are the app's EHR metadata, so the labelled option set crosses as the
 * ``options`` prop.
 */

import { Button } from '@heidi/design-system/button';
import { Input } from '@heidi/design-system/input';
import { SearchInput } from '@heidi/design-system/search-input';
import { TypographyCaption } from '@heidi/design-system/typography';
import { useQueryIntegrationProviders } from '@heidi/integrations/ehr/data';
import { EHRProvider } from '@heidi/schema/ml-scribe';

import { cn } from '@heidi/utils';
import { Check, Hospital } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { getWorkHostPort } from '../../work-home/work-home-ports';

export interface PickedEhr {
  /** Canonical EHRProvider value — echoed back to the agent verbatim. */
  id: string;
  /** Human-facing provider name (e.g. 'Amazing Charts' for `AmazingCharts`). */
  label: string;
}

/**
 * Sentinel provider for "my EHR isn't listed". Selecting it requires a
 * free-text detail (the actual EHR name) so the agent learns which system
 * the clinician uses — mirrors the integration setup flow's `otherValue`
 * requirement in `ehr-general-card.tsx`.
 */
export const EHR_OTHER_PROVIDER_ID: string = EHRProvider.Other;

export interface EhrPickerListProps {
  /** Currently-armed provider id (highlights the row). */
  selectedId?: string;
  onSelect: (ehr: PickedEhr) => void;
  /** Free-text detail shown/required when "Other" is the armed provider. */
  otherValue?: string;
  onOtherValueChange?: (value: string) => void;
  disabled?: boolean;
  /** Cap the visible list height; the list scrolls past this. */
  className?: string;
}

/** What the app supplies: the code-owned provider set, labelled and sorted. */
export interface EhrPickerListHost {
  /** Built once at module scope by the host, so its identity is stable. */
  options: readonly PickedEhr[];
}

export const EhrPickerListView = ({
  selectedId,
  onSelect,
  otherValue,
  onOtherValueChange,
  disabled = false,
  className,
  options,
}: EhrPickerListProps & EhrPickerListHost): JSX.Element => {
  const intl = useIntl();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  return (
    <div className={cn('flex flex-col gap-2', className)} data-testid="ehr-picker">
      <SearchInput
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled}
        placeholder={intl.formatMessage({
          defaultMessage: 'Search EHRs',
          id: 'r9mQxCkBD9',
        })}
        aria-label={intl.formatMessage({
          defaultMessage: 'Search EHRs',
          id: 'r9mQxCkBD9',
        })}
        clearAriaLabel={intl.formatMessage({
          defaultMessage: 'Clear search',
          id: '4YJHutR26p',
        })}
      />

      <div className="flex max-h-64 flex-col gap-1 overflow-y-auto [scrollbar-gutter:stable]">
        {filtered.length === 0 ? (
          <TypographyCaption className="px-3 py-4 text-text-secondary">
            {intl.formatMessage({
              defaultMessage: 'No EHRs match your search.',
              id: 'bERPD+li7q',
            })}
          </TypographyCaption>
        ) : (
          filtered.map((o) => {
            const isSelected = o.id === selectedId;
            return (
              <button
                key={o.id}
                type="button"
                disabled={disabled}
                onClick={() => onSelect(o)}
                aria-pressed={isSelected}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
                  'disabled:cursor-not-allowed disabled:opacity-60',
                  isSelected
                    ? 'border-border-pressed bg-background-secondary'
                    : 'border-border bg-surface hover:bg-surface-hover'
                )}
                data-testid="ehr-picker-row"
              >
                <Hospital className="size-4 shrink-0 text-text-secondary" />
                <span className="min-w-0 flex-1 truncate text-sm text-text-primary">
                  {o.label}
                </span>
                {isSelected && (
                  <Check className="size-4 shrink-0 text-text-active" aria-hidden />
                )}
              </button>
            );
          })
        )}
      </div>

      {selectedId === EHR_OTHER_PROVIDER_ID && (
        <Input
          value={otherValue ?? ''}
          onChange={(e) => onOtherValueChange?.(e.target.value)}
          disabled={disabled}
          autoFocus
          placeholder={intl.formatMessage({
            defaultMessage: 'Type the name of your EHR…',
            id: 'htU2Bagxt4',
          })}
          aria-label={intl.formatMessage({
            defaultMessage: 'Other EHR name',
            id: 'V0agZeqvmZ',
          })}
          className="h-9 text-sm"
          data-testid="ehr-picker-other-input"
        />
      )}
    </div>
  );
};

/** Binds the EHR picker to the provider options. */
export const EhrPickerList = (props: EhrPickerListProps): JSX.Element => {
  const intl = useIntl();
  const shell = getWorkHostPort('shell');
  const isCountryScopedEhrList = shell.useIsCountrySpecificEhrSelectionEnabled();
  const userCountryCode = shell.useUserCountryCode();
  const providersQuery = useQueryIntegrationProviders({
    country: userCountryCode,
    enabled: isCountryScopedEhrList && !!userCountryCode,
  });

  const options: readonly PickedEhr[] = useMemo(() => {
    const enumOptions: readonly PickedEhr[] = Object.values(EHRProvider)
      .filter((provider) => provider !== EHRProvider.Heidi)
      .map((provider) => ({
        id: provider,
        label:
          getWorkHostPort('integrations').getEhrProviderMetadata(provider).displayName,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (!isCountryScopedEhrList) return enumOptions;
    const otherOption: PickedEhr = {
      id: EHR_OTHER_PROVIDER_ID,
      label: intl.formatMessage(shell.personalizationSharedMessages.other),
    };
    if (!userCountryCode || providersQuery.isError) return [otherOption];
    if (!providersQuery.isSuccess) return [];
    return [
      ...providersQuery.data.map((provider) => ({
        id: provider.name,
        label: provider.name,
      })),
      otherOption,
    ];
  }, [
    isCountryScopedEhrList,
    userCountryCode,
    providersQuery.isError,
    providersQuery.isSuccess,
    providersQuery.data,
    intl,
    shell.personalizationSharedMessages.other,
  ]);

  return (
    <>
      <EhrPickerListView {...props} options={options} />
      {isCountryScopedEhrList && providersQuery.isError ? (
        <div className="flex items-center gap-1.5 pl-0.5">
          <TypographyCaption className="text-validation-destructive">
            {intl.formatMessage({
              defaultMessage: "We couldn't load the EHR list.",
              id: 'k2ef/l1mnS',
            })}
          </TypographyCaption>
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0"
            onClick={() => void providersQuery.refetch()}
          >
            {intl.formatMessage({ defaultMessage: 'Try again', id: 'FazwRldA7z' })}
          </Button>
        </div>
      ) : null}
    </>
  );
};
